import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import { LoadingButton } from "@mui/lab";
import * as R from "ramda";
import RepresentmentInfoText from "./components/representment-info-text";
import MissingCompanyIdWarning from "./components/missing-company-id";
import { processFiles, verifyFiles } from "./api/chargeback-api";
import FileList from "./components/files/file-list";
import RepresentmentGraphism from "./components/icon/graphism";
import { useSearchParams } from "react-router-dom";
import { hasFileErrors } from "modules/representment/selectors/representment-selectors";
import RepresentmentSuccess from "./components/representment-success";
import { sendRepresentmentSuccessEvent, sendRepresentmentErrorEvent } from "./metrics/datalayer";

const UploadButton = styled(LoadingButton)`
  color: #65a300;
  border-color: #65a300;
  padding: 8px 24px;
  font-weight: 700;
`;

const SendButton = styled(LoadingButton)`
  margin-top: 32px;
  margin-bottom: 60px;
  padding: 8px 24px;
  background-color: #65a300;
  font-weight: 700;
`;

const Graphism = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;

  img {
    height: auto;
    width: 100%;
    display: block;
  }
`;

const UploadIcon = () => <i className="fas fa-upload"></i>;

const isSendButtonDisabled = (isLoading, isSendLoading, files, errors) => {
  return (
    R.isEmpty(files) || isLoading || isSendLoading || hasFileErrors(errors)
  );
};

const FETCH_STATES = {
  SUCCESS: "success",
  SEND_LOADING: "send_loading",
  LOADING: "loading",
  ERROR: "error",
  DEFAULT: "default",
};

export default function Representment() {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fetchState, setFetchState] = useState(FETCH_STATES.DEFAULT);
  const [apiResponse, setApiResponse] = useState(null);
  const [searchParams] = useSearchParams();
  const isLoading = fetchState === FETCH_STATES.LOADING;
  const isSendLoading = fetchState === FETCH_STATES.SEND_LOADING;
  const isSuccessful = fetchState === FETCH_STATES.SUCCESS;

  const sendButtonDisabled = isSendButtonDisabled(
    isLoading,
    isSendLoading,
    uploadedFiles,
    apiResponse
  );

  const handleUploadFiles = async (files) => {
    const uploaded = [...uploadedFiles];
    files.forEach((file) => {
      if (uploaded.findIndex((f) => f.name === file.name) === -1) {
        uploaded.push(file);
      }
    });

    setFetchState(FETCH_STATES.LOADING);
    const verification = await verifyFiles(getCompanyId(), uploaded);
    if (verification.success) {
      setFetchState(FETCH_STATES.DEFAULT);
      setApiResponse(null);
    } else {
      setFetchState(FETCH_STATES.DEFAULT);
      setApiResponse(verification.data);
    }
    setUploadedFiles(uploaded);
  };

  const handleVerifyFiles = async function (files) {
    if (!R.isEmpty(files)) {
      try {
        setFetchState(FETCH_STATES.LOADING);
        const verification = await verifyFiles(getCompanyId(), files);
        if (verification.success) {
          setFetchState(FETCH_STATES.DEFAULT);
          setApiResponse(null);
        } else {
          setFetchState(FETCH_STATES.DEFAULT);
          setApiResponse(verification.data);
        }
      } catch (error) {
        setFetchState(FETCH_STATES.ERROR);
        setApiResponse(error);
      }
    }
  };

  const handleFileEvent = (event) => {
    const chosenFiles = Array.prototype.slice.call(event.target.files);
    handleUploadFiles(chosenFiles);
  };

  const deleteFile = (index) => {
    const modified = R.remove(index, 1, uploadedFiles);
    setUploadedFiles(modified);
    handleVerifyFiles(modified);
  };

  const hasCompanyId = function () {
    const notNil = R.complement(R.isNil);
    return notNil(getCompanyId());
  };

  const getCompanyId = function () {
    return searchParams.get("companyId");
  };

  const sendFiles = async () => {
    try {
      setFetchState(FETCH_STATES.SEND_LOADING);
      await processFiles(getCompanyId(), uploadedFiles);
      sendRepresentmentSuccessEvent();
      setFetchState(FETCH_STATES.SUCCESS)
    } catch (error) {
      setFetchState(FETCH_STATES.ERROR);
      sendRepresentmentErrorEvent();
    }
  };

  const resetState = () => {
    setFetchState(FETCH_STATES.DEFAULT);
    setApiResponse(null);
    setUploadedFiles([]);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {!hasCompanyId(searchParams) ? (
            <MissingCompanyIdWarning />
          ) : hasCompanyId(searchParams) && isSuccessful ? (
            <RepresentmentSuccess
              files={uploadedFiles}
              onClickNewRepresentment={resetState}
            />
          ) : (
            <RepresentmentInfoText />
          )}
        </Grid>
        <Grid
          item
          md={6}
          sx={{ display: { xs: "none", sm: "none", md: "block" } }}
        >
          <Graphism>
            <RepresentmentGraphism />
          </Graphism>
        </Grid>
      </Grid>
      {hasCompanyId(searchParams) && !isSuccessful && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <UploadButton
                id="upload-files"
                variant="outlined"
                component="label"
                disabled={isLoading || isSendLoading || isSuccessful}
                loading={isLoading}
                loadingPosition="start"
                startIcon={<UploadIcon />}
                key="material-upload-button"
              >
                Carregar Arquivo
                <input
                  type="file"
                  name="transaction_file"
                  key="input-file"
                  accept=".pdf,.jpg,image/jpg,application/pdf"
                  multiple
                  hidden
                  onChange={handleFileEvent}
                />
              </UploadButton>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FileList
                files={uploadedFiles}
                onDelete={deleteFile}
                fetchState={fetchState}
                apiResponse={apiResponse}
              />
              <SendButton
                variant="contained"
                onClick={sendFiles}
                disabled={sendButtonDisabled}
                loading={isSendLoading}
                id="process-representation"
              >
                Enviar
              </SendButton>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
